import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Minus, Plus, Star, ShoppingBag,
  Gem, Leaf, RotateCcw, Lock, Feather, Sparkles, Sun,
  Droplets, Box, Clock, ChevronRight, Check,
} from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/Reveal";
import ProductGallery from "@/components/ProductGallery";
import FrequentlyBoughtTogether from "@/components/FrequentlyBoughtTogether";
import DesignVariants from "@/components/DesignVariants";
import SeoHead from "@/components/SeoHead";
import JsonLd, { buildProductSchema, buildBreadcrumbSchema } from "@/components/JsonLd";
import { useBranding } from "@/context/BrandingContext";
import ThemedProductDetail from "@/pages/ThemedProductDetail";
import { getTheme } from "@/lib/themes";

const RECENT_KEY = "rieave.recent";

const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const pushRecent = (p) => {
  try {
    const slim = { id: p.id, slug: p.slug, name: p.name, price: p.price, image: (p.images || [])[0] };
    const all = readRecent().filter((x) => x.id !== p.id);
    all.unshift(slim);
    localStorage.setItem(RECENT_KEY, JSON.stringify(all.slice(0, 10)));
  } catch {}
};

const TrustBadge = ({ icon: Icon, title, sub }) => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0 w-11 h-11 rounded-full bg-white flex items-center justify-center shadow-sm border border-border/40">
      <Icon size={18} className="text-rose" strokeWidth={1.5} />
    </div>
    <div>
      <p className="font-body font-medium text-sm text-charcoal leading-tight">{title}</p>
      <p className="font-body text-xs text-mauve">{sub}</p>
    </div>
  </div>
);

const LoveItCard = ({ icon: Icon, title, body, delay }) => (
  <div
    className="flex items-start gap-4 p-5 bg-white rounded-2xl shadow-sm border border-border/40 animate-fade-up"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-lavender/40 flex items-center justify-center">
      <Icon size={22} className="text-rose" strokeWidth={1.5} />
    </div>
    <div>
      <h3 className="font-hero text-lg text-charcoal mb-1">{title}</h3>
      <p className="font-body text-sm text-mauve">{body}</p>
    </div>
  </div>
);

const CareCard = ({ icon: Icon, title, body, delay }) => (
  <div
    className="bg-white rounded-2xl p-6 text-center shadow-sm border border-border/40 animate-fade-up"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="w-14 h-14 rounded-full bg-lavender/40 flex items-center justify-center mx-auto mb-4">
      <Icon size={26} className="text-rose" strokeWidth={1.5} />
    </div>
    <h3 className="font-hero text-lg text-charcoal mb-2">{title}</h3>
    <p className="font-body text-sm text-mauve">{body}</p>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [recent, setRecent] = useState([]);
  const { addItem } = useCart();
  const { customer } = useAuth();
  const { branding } = useBranding();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setSize((r.data.sizes || [])[0] || null);
      setQty(1);
      pushRecent(r.data);
      api.get(`/reviews?product_id=${r.data.id}`).then((rr) => setReviews(rr.data)).catch(() => {});
      api
        .get(`/products`, { params: { category: r.data.category_slug, limit: 12 } })
        .then((rr) => setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 6)))
        .catch(() => {});
    }).catch(() => {});
    setRecent(readRecent().filter((x) => x.slug !== slug).slice(0, 6));
  }, [slug]);

  if (!product)
    return <div className="rieave-container py-24 text-center font-cormorant text-xl text-mauve">Loading…</div>;

  // If this product has a character theme, render the themed PDP instead
  if (product.theme_slug && getTheme(product.theme_slug)) {
    return (
      <ThemedProductDetail
        product={product}
        onAddToCart={(s, q) => addItem(product, q || qty, s || size)}
        onAddToWishlist={async () => {
          if (!customer) { toast("Please sign in to save favourites"); return; }
          try { await api.post(`/wishlist/${product.id}`); toast.success("Saved to wishlist"); }
          catch { toast.error("Could not save"); }
        }}
      />
    );
  }

  const discount =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;

  const addToWishlist = async () => {
    if (!customer) { toast("Please sign in to save favourites"); return; }
    try { await api.post(`/wishlist/${product.id}`); toast.success("Saved to wishlist"); }
    catch { toast.error("Could not save"); }
  };

  // Rating buckets for breakdown bars
  const ratingBreakdown = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));
  const totalReviews = reviews.length || 0;

  const images = (product.images || []).length ? product.images : [""];

  return (
    <div className="pb-24 md:pb-0" data-testid="product-detail-page">
      <SeoHead
        title={product.seo_title || `${product.name} — Rieave`}
        description={product.seo_description || product.short_description || `Discover ${product.name} — handcrafted by Rieave.`}
        image={(product.images || [])[0]}
        canonical={typeof window !== "undefined" ? window.location.origin + `/product/${product.slug}` : ""}
      />
      <JsonLd
        data={[
          buildProductSchema(product, { brandName: branding?.brand_name ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1) : "Rieave" }),
          buildBreadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: "Shop", url: typeof window !== "undefined" ? window.location.origin + "/shop" : "/shop" },
            ...(product.category_slug ? [{ name: product.category_slug.charAt(0).toUpperCase() + product.category_slug.slice(1), url: typeof window !== "undefined" ? window.location.origin + `/shop/${product.category_slug}` : `/shop/${product.category_slug}` }] : []),
            { name: product.name, url: typeof window !== "undefined" ? window.location.origin + `/product/${product.slug}` : `/product/${product.slug}` },
          ]),
        ].filter(Boolean)}
      />
      {/* BREADCRUMB */}
      <div className="rieave-container pt-10 pb-4">
        <nav className="flex items-center gap-1.5 font-body text-sm text-mauve">
          <Link to="/" className="hover:text-charcoal">Home</Link>
          <ChevronRight size={14} />
          <Link to="/shop" className="hover:text-charcoal">Collections</Link>
          <ChevronRight size={14} />
          <span className="text-charcoal">{product.name}</span>
        </nav>
      </div>

      {/* MAIN — gallery + details */}
      <section className="py-6 md:py-10">
        <div className="rieave-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            {/* Gallery — zoom on hover, auto-cycle when idle */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <ProductGallery images={images} alt={product.name} />
            </motion.div>

            {/* Details — sticky, staggered entrance */}
            <motion.div
              className="lg:sticky lg:top-28 lg:self-start"
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
            >
              <motion.div
                className="space-y-6"
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
              >
                <motion.div variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}>
                  <p className="eyebrow mb-2">{product.category_slug}</p>
                  <h1 className="font-hero text-3xl md:text-4xl text-charcoal mb-2" data-testid="product-detail-name">
                    {product.name}
                  </h1>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-gold">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <motion.span
                          key={i}
                          initial={{ scale: 0, rotate: -45 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ delay: 0.25 + i * 0.06, type: "spring", stiffness: 320, damping: 14 }}
                        >
                          <Star
                            size={15}
                            fill={i < Math.round(product.rating) ? "currentColor" : "none"}
                            stroke="currentColor"
                            className={i < Math.round(product.rating) ? "text-gold" : "text-mauve/40"}
                          />
                        </motion.span>
                      ))}
                    </div>
                    <span className="text-sm text-mauve">
                      {product.rating} ({product.review_count} reviews)
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-baseline gap-3"
                  variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                >
                  <span className="font-hero text-3xl md:text-[34px] text-charcoal" data-testid="product-detail-price">
                    {formatINR(product.price)}
                  </span>
                  {product.compare_at_price && product.compare_at_price > product.price && (
                    <>
                      <span className="font-body text-lg text-mauve line-through">
                        {formatINR(product.compare_at_price)}
                      </span>
                      <motion.span
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.5, type: "spring", stiffness: 260, damping: 16 }}
                        className="bg-gold/40 text-charcoal font-label text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full"
                      >
                        {discount}% off
                      </motion.span>
                    </>
                  )}
                </motion.div>

                <motion.p
                  className="font-body text-charcoal/75 leading-relaxed"
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                >
                  {product.short_description}
                </motion.p>

                {product.sizes && product.sizes.length > 0 && (
                  <motion.div variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}>
                    <p className="text-sm font-medium text-charcoal mb-3">Size</p>
                    <div className="flex gap-2 flex-wrap">
                      {product.sizes.map((s) => (
                        <motion.button
                          key={s}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSize(s)}
                          className={`px-4 py-2 rounded-full border transition text-sm ${
                            size === s ? "bg-charcoal text-cream border-charcoal" : "border-border text-charcoal hover:border-charcoal"
                          }`}
                          data-testid={`size-option-${s}`}
                        >
                          {s}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}

                <motion.div
                  className="flex items-center gap-4"
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                >
                  <span className="text-sm font-medium text-charcoal">Quantity</span>
                  <div className="flex items-center border border-border rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      disabled={qty <= 1}
                      className="w-10 h-10 flex items-center justify-center hover:bg-blush/40 transition disabled:opacity-50"
                      aria-label="minus"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-12 text-center font-body text-charcoal" data-testid="product-qty">{qty}</span>
                    <button
                      onClick={() => setQty((q) => q + 1)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-blush/40 transition"
                      aria-label="plus"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </motion.div>

                <motion.div
                  className="flex items-center gap-2"
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                >
                  {product.stock > 0 ? (
                    <>
                      <motion.span
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 2.5, ease: "easeInOut" }}
                        className="inline-flex"
                      >
                        <Check size={16} className="text-emerald-600" />
                      </motion.span>
                      <span className="text-sm font-medium text-emerald-600">In stock</span>
                    </>
                  ) : (
                    <span className="text-sm font-medium text-rose">Sold out</span>
                  )}
                </motion.div>

                <motion.div
                  className="flex gap-3 pt-1"
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                >
                  <button
                    onClick={() => addItem(product, qty, size)}
                    disabled={product.stock < 1}
                    className="relative flex-1 h-14 rounded-xl px-10 text-base md:text-lg bg-lavender hover:bg-lavender/80 text-charcoal font-body font-medium inline-flex items-center justify-center gap-2"
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingBag size={18} />
                    {product.stock < 1 ? "Sold out" : "Add to cart"}
                  </button>
                  <button
                    onClick={addToWishlist}
                    className="w-14 h-14 rounded-xl border border-border bg-white hover:bg-blush/40 flex items-center justify-center"
                    aria-label="wishlist"
                    data-testid="add-to-wishlist-button"
                  >
                    <Heart size={18} />
                  </button>
                </motion.div>

                {/* Trust badges */}
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } }}
                  className="bg-blush/40 rounded-2xl p-5 mt-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TrustBadge icon={Gem} title="Premium materials" sub={product.materials || "18k gold plated"} />
                    <TrustBadge icon={Leaf} title="Skin-friendly" sub="Hypoallergenic" />
                    <TrustBadge icon={RotateCcw} title="Easy returns" sub="30-day policy" />
                    <TrustBadge icon={Lock} title="Secure checkout" sub="SSL encrypted" />
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* DESIGN VARIANTS — siblings via shared variant_group */}
      <DesignVariants slug={slug} variant="default" />

      {/* FREQUENTLY BOUGHT TOGETHER */}
      <FrequentlyBoughtTogether slug={product.slug} />

      {/* WHY YOU'LL LOVE IT */}
      <section className="py-12 bg-cream" data-testid="why-love-section">
        <div className="rieave-container">
          <Reveal>
            <h2 className="font-hero text-3xl md:text-4xl text-charcoal text-center mb-10">
              Why you'll <span className="serif-italic text-rose">love it</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <LoveItCard icon={Feather} title="Lightweight & comfortable" body="Designed for all-day wear without discomfort." delay={0} />
            <LoveItCard icon={Sparkles} title="Tarnish resistant" body="Maintains its shine for years to come." delay={80} />
            <LoveItCard icon={Sun} title="Designed for daily wear" body="Durable enough for everyday moments." delay={160} />
            <LoveItCard icon={Heart} title="Thoughtfully crafted" body="Made with attention to every detail." delay={240} />
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="py-14">
        <div className="rieave-container max-w-4xl">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="w-full justify-start border-b border-border bg-transparent h-auto p-0 mb-8 rounded-none flex-wrap">
              <TabsTrigger
                value="description"
                className="px-6 py-3 text-sm font-body text-mauve data-[state=active]:text-charcoal data-[state=active]:border-b-2 data-[state=active]:border-rose rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Description
              </TabsTrigger>
              <TabsTrigger
                value="material"
                className="px-6 py-3 text-sm font-body text-mauve data-[state=active]:text-charcoal data-[state=active]:border-b-2 data-[state=active]:border-rose rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Material & care
              </TabsTrigger>
              <TabsTrigger
                value="shipping"
                className="px-6 py-3 text-sm font-body text-mauve data-[state=active]:text-charcoal data-[state=active]:border-b-2 data-[state=active]:border-rose rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Shipping & returns
              </TabsTrigger>
              <TabsTrigger
                value="reviews"
                className="px-6 py-3 text-sm font-body text-mauve data-[state=active]:text-charcoal data-[state=active]:border-b-2 data-[state=active]:border-rose rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                Reviews <span className="ml-2 text-xs bg-blush/60 px-2 py-0.5 rounded-full">{totalReviews}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="description" className="mt-2">
              <p className="font-body text-charcoal/75 leading-[1.85] whitespace-pre-line max-w-3xl">
                {product.description || product.short_description}
              </p>
            </TabsContent>

            <TabsContent value="material" className="mt-2 space-y-3">
              <p className="font-body text-charcoal/75 leading-relaxed">
                <span className="font-medium text-charcoal">Material: </span>
                {product.materials || "Crafted with premium materials selected for lasting beauty."}
              </p>
              <ul className="font-body text-charcoal/75 leading-relaxed list-disc pl-5 space-y-1.5">
                <li>Keep dry — remove before swimming, showering or exercising.</li>
                <li>Apply perfume, lotion and makeup before wearing.</li>
                <li>Wipe gently with a soft, dry cloth after each wear.</li>
                <li>Store in the provided soft pouch away from direct sunlight.</li>
              </ul>
            </TabsContent>

            <TabsContent value="shipping" className="mt-2 space-y-3">
              <p className="font-body text-charcoal/75 leading-relaxed">
                Free, tracked shipping on all orders above ₹1,499 within India. International shipping available at checkout.
                Every piece is dispatched in our signature box, ready to gift.
              </p>
              <p className="font-body text-charcoal/75 leading-relaxed">
                <span className="font-medium text-charcoal">Returns: </span>
                Not quite right? Return any unworn piece within 30 days for a full refund or exchange.
              </p>
            </TabsContent>

            <TabsContent value="reviews" className="mt-2">
              {/* Rating breakdown */}
              <div className="bg-white rounded-2xl border border-border/40 p-6 mb-6 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-center">
                <div className="text-center md:border-r md:border-border md:pr-6">
                  <p className="font-hero text-5xl text-charcoal leading-none">{product.rating.toFixed(1)}</p>
                  <div className="flex justify-center text-gold my-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} fill={i < Math.round(product.rating) ? "currentColor" : "none"} stroke="currentColor" />
                    ))}
                  </div>
                  <p className="font-label text-xs tracking-[0.2em] uppercase text-mauve">
                    {totalReviews === 0 ? "No reviews yet" : `${totalReviews} review${totalReviews > 1 ? "s" : ""}`}
                  </p>
                </div>
                <div className="space-y-2">
                  {ratingBreakdown.map(({ star, count }) => {
                    const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-3 text-sm">
                        <span className="text-charcoal w-6">{star}★</span>
                        <div className="flex-1 h-2 bg-lavender/60 rounded-full overflow-hidden">
                          <div className="h-full bg-rose transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-mauve w-6 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {reviews.length === 0 ? (
                <p className="text-center text-mauve py-6">Be the first to review this piece.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reviews.slice(0, 8).map((r) => (
                    <div key={r.id} className="bg-white rounded-2xl p-5 border border-border/40">
                      <div className="flex text-gold mb-1.5">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <Star key={i} size={13} fill="currentColor" stroke="none" />
                        ))}
                      </div>
                      <p className="font-cormorant text-lg text-charcoal">{r.title}</p>
                      <p className="text-sm text-charcoal/75 mt-1">{r.body}</p>
                      <p className="font-label text-[11px] uppercase tracking-[0.22em] text-mauve mt-2">— {r.author_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* STYLE IT WITH */}
      {related.length > 0 && (
        <section className="py-12 bg-lavender/20" data-testid="style-with-section">
          <div className="rieave-container">
            <Reveal>
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-hero text-3xl md:text-4xl text-charcoal">
                  Style it <span className="serif-italic text-rose">with</span>
                </h2>
              </div>
            </Reveal>
            <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-4">
              {related.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="flex-shrink-0"
                >
                  <Link to={`/product/${p.slug}`} className="block w-52 md:w-60 group">
                    <div className="relative aspect-square rounded-2xl overflow-hidden bg-white shadow-sm mb-3 border border-border/40">
                      {(p.images || [])[0] && (
                        <img
                          src={p.images[0]} alt={p.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                    </div>
                    <h3 className="font-cormorant text-base md:text-lg text-charcoal group-hover:text-rose transition-colors leading-tight">
                      {p.name}
                    </h3>
                    <p className="font-body text-sm text-mauve">{formatINR(p.price)}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CARE & LONGEVITY */}
      <section className="py-12 bg-cream" data-testid="care-section">
        <div className="rieave-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="font-hero text-3xl md:text-4xl text-charcoal mb-3">
              Care & <span className="serif-italic text-rose">longevity</span>
            </h2>
            <p className="font-body text-mauve">
              With a little care, your {product.name} will stay as beautiful as the day it arrived.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
            <CareCard icon={Droplets} title="Keep dry" body="Remove before swimming, showering or exercising." delay={0} />
            <CareCard icon={Box} title="Store softly" body="Keep in the provided pouch when not being worn." delay={80} />
            <CareCard icon={Clock} title="Apply last" body="Put on jewelry after lotions, perfume and makeup." delay={160} />
            <CareCard icon={Sparkles} title="Clean gently" body="Wipe with a soft, dry cloth to maintain shine." delay={240} />
          </div>
          <div className="max-w-2xl mx-auto mt-10 p-6 bg-white rounded-2xl border border-border/40 shadow-sm">
            <p className="font-body text-center text-mauve">
              <span className="font-medium text-charcoal">Longevity promise: </span>
              our pieces are designed for daily wear. Following these simple habits will keep them looking freshly polished.
            </p>
          </div>
        </div>
      </section>

      {/* RECENTLY VIEWED */}
      {recent.length > 0 && (
        <section className="py-12 bg-white" data-testid="recently-viewed-section">
          <div className="rieave-container">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-hero text-3xl md:text-4xl text-charcoal">Recently viewed</h2>
              <Link to="/shop" className="btn-link hidden md:inline-flex">Continue shopping →</Link>
            </div>
            <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-4">
              {recent.map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} className="flex-shrink-0 w-44 md:w-48 group">
                  <div className="aspect-square rounded-2xl overflow-hidden bg-blush/30 mb-3 border border-border/40">
                    {p.image && (
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                  </div>
                  <h3 className="font-cormorant text-base text-charcoal group-hover:text-rose transition-colors truncate">
                    {p.name}
                  </h3>
                  <p className="font-body text-sm text-mauve">{formatINR(p.price)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MOBILE STICKY BOTTOM BAR */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 shadow-lg md:hidden z-40" data-testid="mobile-sticky-bar">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-hero text-xl text-charcoal leading-none">{formatINR(product.price)}</p>
            <p className="text-xs text-mauve truncate max-w-[150px]">{product.name}</p>
          </div>
          <button
            onClick={() => addItem(product, qty, size)}
            disabled={product.stock < 1}
            className="h-12 rounded-xl px-6 flex-1 max-w-[220px] bg-lavender hover:bg-lavender/80 text-charcoal font-body font-medium inline-flex items-center justify-center gap-2"
            data-testid="mobile-add-to-cart"
          >
            <ShoppingBag size={17} /> Add to cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
